import {
	Breadcrumb,
	CommandButton,
	IBreadcrumbItem,
	IIconProps,
	Label,
	mergeStyleSets,
	SelectionMode,
	Separator,
	Stack,
	Theme,
	useTheme,
} from '@fluentui/react';
import {DetailsField, DetailsSection, EntityList} from 'components';
import {CommentForm} from 'components/CommentForm';
import {CommentLevel} from 'components/CommentList';
import {FAQForm} from 'components/FAQForm';
import {FAQLevel} from 'components/FAQList';
import {LoadWrapper} from 'components/LoadWrapper';
import {RegDocsList} from 'features/RegulatoryDocuments/components/RegDocsList/RegDocsList';
import {RegulatoryDocumentsListDocumentForm} from 'features/RegulatoryDocuments/components/RegulatoryDocumentsListDocumentForm';
import {CompareVersionPanel} from 'features/RegulatoryDocuments/components/DocumentCompare';
import {RegulatoryDocumentsContextProvider} from 'features/RegulatoryDocuments/context';
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {createSearchParams, useNavigate, useParams} from 'react-router-dom';
import {
	Keyword,
	Market,
	PogisDocument,
	Regulation,
	User,
	UserRole,
	WorkflowStatus,
} from 'types';
import {useGetRegulationQuery} from '../hooks';
import 'react-document-timeline/lib/style.css';
import {useEditRegulationCmd} from '../components/useEditRegulationCmd';
import {
	RegulationFormModeStateInfo,
	useRegulationFormMode,
} from '../components/useRegulationFormMode';
import {useSortRegulatoryDocs} from '../components/useSortRegulatoryDocs';
import RegulationDetailRegulationForm from '../components/RegulationDetailRegulationForm';
import {
	StateForShouldShowAudit,
	useShouldShowAudit,
	useToggleAuditCmd,
	useFavoriteCommand,
} from 'hooks';
import {RegDocsTooltipTranslationProvider} from 'features/RegulatoryDocuments/components/RegDocTooltipTranslationProvider';
import {KeywordsField} from 'components/DetailsSection/KeywordsField';
import {PageRegulatoryDocs} from './RegulationDetail.types';
import {EntityListContextProvider} from '../../../components/EntityList/EntityListContext';
import {usePogisPageColumn} from 'features/Archive/PogisPageColumns';
import {PogisDocumentsTooltipTranslationProvider} from 'features/Archive/components/PogisDocumentTranslationTooltipProvider';
import {funcMapPogisDocument} from 'features/Archive/PogisDocumentPage';
import {VkosPersona} from 'components/Personas';
import {useGetAllVkosQuery} from 'features/AdminSection/hooks/vko.generated';
import {useGetAllKeywordsQuery} from 'features/AdminSection/hooks/keywords.generated';
import {useUserContext} from 'authentication/UserContext';

export const RegulationDetail = () => {
	const {t} = useTranslation('features/regulations', {
		keyPrefix: 'RegulationDetail',
	});
	const {t: tt} = useTranslation('features/regulations', {
		keyPrefix: 'TooltipsText',
	});
	const stateForShouldShowAudit: StateForShouldShowAudit = useShouldShowAudit();
	const [showAudit] = stateForShouldShowAudit;

	const {regulationId}: any = useParams();
	const {isAvk, isBav, isVko} = useUserContext();
	const isAvkOrBav = isAvk || isBav;

	const theme = useTheme();
	const classnames = getClassnames(theme);

	const {loading, data: regulation} = useGetRegulationQuery({
		variables: {regulationId},
	});
	const {data: keywordData} = useGetAllKeywordsQuery();
	const {data: dataVkos} = useGetAllVkosQuery();

	const auditLogs = React.useMemo(
		() => regulation?.regulation?.auditLog || [],
		[regulation],
	);

	const regulationData = React.useMemo(
		() => regulation?.regulation || ({} as Regulation),
		[regulation],
	);

	const regulatoryDocuments: PageRegulatoryDocs = useSortRegulatoryDocs({
		docs: regulationData.regulatoryDocuments as any,
		extraDependencies: [regulationData],
	});

	const comments = React.useMemo(
		() => regulationData.comments || [],
		[regulationData],
	);

	const faqs = React.useMemo(() => regulationData.faqs || [], [regulationData]);

	const navigate = useNavigate();
	const onBreadcrumbClick = React.useCallback(
		(
			ev?: React.MouseEvent<HTMLElement, MouseEvent> | undefined,
			item?: IBreadcrumbItem | undefined,
		) => {
			ev?.preventDefault();
			if (item && item.href) {
				navigate(item.href);
			}
		},
		[],
	);

	const itemsWithHref: IBreadcrumbItem[] = [
		{
			text: t('Regulations'),
			key: 'regulations',
			href: '/regulations',
			onClick: onBreadcrumbClick,
		},
		{
			text: `${regulationData?.regulationNumber}`,
			key: 'version',
		},
	];

	const vkosPersonaField: DetailsField[] = useMemo(() => {
		if (!loading && regulation?.regulation) {
			const users = regulation?.regulation?.contactPersons as User[];
			const keywords = (keywordData?.keywords ?? []) as Keyword[];
			const data = (
				<VkosPersona users={users} keywords={keywords} dataVkos={dataVkos} />
			);

			return [
				{
					data,
					label: t('ContactPersonLabel'),
					type: 'custom',
				},
			];
		}

		return [];
	}, [regulation, keywordData, dataVkos]);

	const initialFieldRows: DetailsField[][] = useMemo(() => {
		const regulationClusterOrIcsField: DetailsField = isAvkOrBav
			? {
					data: regulationData?.icsCodes,
					type: 'array',
					label: t('IcsCodesLabel'),
			  }
			: {
					data: regulationData?.regulationClusters,
					type: 'array',
					label: t('RegulationClustersLabel'),
					tooltipContent: tt('regulationClusters'),
			  };

		const sourceOrVehicleCategoriesField: DetailsField = isAvkOrBav
			? {
					data: regulationData?.source,
					type: 'text',
					label: t('SourceLabel'),
			  }
			: {
					data: regulationData?.vehicleCategories,
					type: 'array',
					label: t('VehicleCategoriesLabel'),
			  };

		const driveVariantField: DetailsField | null = isVko
			? {
					data: regulationData?.driveVariants,
					type: 'array',
					label: t('DriveVariantsLabel'),
			  }
			: null;

		return [
			[
				{
					data: regulationData?.summary,
					type: 'richtext',
					label: t('ShortSummaryLabel'),
					audit: showAudit,
					auditFieldName: 'summary',
				},
			],
			[
				{
					data: regulationData?.markets,
					type: 'array',
					label: t('MarketsLabel'),
					audit: showAudit,
					auditFieldName: 'marketRefs',
				},
				regulationClusterOrIcsField,
				{
					data: regulationData?.mainKeywords,
					type: 'array',
					label: t('MainKeywordsLabel'),
					audit: showAudit,
					auditFieldName: 'mainKeywordRefs',
				},
				{
					data: regulationData?.keywords,
					label: t('KeyWordsLabel'),
					render: KeywordsField,
				},
			],
			[
				...vkosPersonaField,
				sourceOrVehicleCategoriesField,
				...(driveVariantField ? [driveVariantField] : []),
				{
					data: regulationData?.standardPlusPcmsClusters,
					type: 'array',
					label: t('StandardPlusPcmsClustersLabel'),
					audit: showAudit,
					auditFieldName: 'standardPlusPcmsClusterRefs',
					tooltipContent: tt('standardPlusPcmsClusters'),
				},
			],
			[
				{
					data: regulationData?.attachments,
					type: 'attachments',
					label: t('AttachmentsLabel'),
				},
			],
		];
	}, [
		regulationData,
		regulatoryDocuments,
		vkosPersonaField,
		showAudit,
		isAvkOrBav,
		isVko,
	]);

	useToggleAuditCmd(stateForShouldShowAudit, {
		roles: [
			UserRole.SystemAdministrator,
			UserRole.Vko,
			UserRole.Vex,
			UserRole.VexSupport,
			UserRole.ShApprover,
			UserRole.Readers,
			UserRole.RegulationReader,
			UserRole.Bav,
			UserRole.Bedarfskoordinator,
			UserRole.Avk,
		],
	});

	useFavoriteCommand(regulationData?.id ?? '', [], {
		roles: [
			UserRole.SystemAdministrator,
			UserRole.Vko,
			UserRole.Vex,
			UserRole.VexSupport,
			UserRole.ShApprover,
			UserRole.Readers,
			UserRole.RegulationReader,
			UserRole.Bav,
			UserRole.Bedarfskoordinator,
			UserRole.Avk,
		],
	});

	const [mode, setMode]: RegulationFormModeStateInfo = useRegulationFormMode();

	useEditRegulationCmd({
		key: 'edit-regulation-data',
		text: t('EditRegulationBtnLabel'),
		priority: 1,
		setMode,
	});

	const pogisDocuments = useMemo(() => {
		const pogisDocumentsNotUnique = (regulationData.regulatoryDocuments ?? [])
			.filter(x => x.workflow?.status !== WorkflowStatus.Modified)
			.flatMap(x => x.pogisDocuments)
			.map(x => funcMapPogisDocument(x as PogisDocument))
			.sort((a, b) => Number(b.pogisId) - Number(a.pogisId));
		return uniqueById(pogisDocumentsNotUnique);
	}, [regulationData]);

	const columnPogis = usePogisPageColumn();

	const onItemInvokedPogis = React.useCallback(
		(item: PogisDocument) => {
			navigate({
				pathname: '/pogisArchive',
				search: createSearchParams({
					id: item.pogisId,
				}).toString(),
			});
		},
		[navigate],
	);

	if (!loading && !regulation?.regulation) {
		return (
			<h4 style={{marginTop: '50px', marginLeft: '20px'}}>
				{t('DocumentNotFound')}
			</h4>
		);
	}

	return (
		<LoadWrapper loading={loading}>
			<div className={classnames.header}>
				<Breadcrumb
					items={itemsWithHref}
					ariaLabel='Breadcrumb'
					overflowAriaLabel='More links'
				/>
			</div>
			<DetailsSection
				auditLogData={auditLogs}
				title={regulationData?.name}
				titleWithAudit={showAudit}
				initialFieldRows={initialFieldRows}
				isHidden={regulationData?.hidden}
			/>
			<div className={classnames.header}>
				<Label className={classnames.headerText}>{t('Documents')}</Label>
			</div>
			<RegulatoryDocumentsContextProvider>
				<RegDocsTooltipTranslationProvider>
					<RegulatoryDocumentsListDocumentForm
						regulationId={regulationId}
						regulatoryDocuments={regulatoryDocuments}
						markets={(regulationData?.markets ?? []) as Market[]}
						regulationNumber={regulationData?.regulationNumber}
					/>
					<EntityListContextProvider pageDetails={regulationData}>
						<div className={classnames.list}>
							<RegDocsList
								regulationId={regulationId}
								regulatoryDocuments={regulatoryDocuments}
								sticky={false}
							/>
						</div>
					</EntityListContextProvider>
				</RegDocsTooltipTranslationProvider>
				<PogisDocumentsTooltipTranslationProvider>
					{!isAvkOrBav && pogisDocuments.length > 0 && (
						<>
							<div className={classnames.header}>
								<Label className={classnames.headerText}>
									{t('PogisEntry')}
								</Label>
							</div>
							<div className={classnames.list}>
								<EntityList
									items={pogisDocuments}
									columns={columnPogis}
									selectionMode={SelectionMode.none}
									onItemInvoked={onItemInvokedPogis}
									hideExcelButton={true}
									hideItemNumber={true}
								/>
							</div>
						</>
					)}
				</PogisDocumentsTooltipTranslationProvider>

				<CompareVersionPanel regulationId={regulationId} />
				<RegulationDetailRegulationForm
					mode={mode}
					setMode={setMode}
					regulation={regulationData}
				/>
			</RegulatoryDocumentsContextProvider>
			<CommentForm comments={comments} level={CommentLevel.Regulation} />
			<FAQForm faqs={faqs} level={FAQLevel.Regulation} />
		</LoadWrapper>
	);
};

const getClassnames = (theme: Theme) =>
	mergeStyleSets({
		header: {
			marginLeft: 15,
		},
		headerText: {
			color: theme.palette.neutralDark,
			fontWeight: 600,
			fontSize: 18,
			lineHeight: 24,
			textTransform: 'capitalize',
		},
		list: {
			width: '98%',
			overflowX: 'auto',
			margin: '5px 15px 25px 15px',
			border: '2px solid ' + theme.palette.neutralLight,
		},
	});

const uniqueById = (arr: any[]) => {
	const map = new Map();
	arr.forEach(item => {
		if (!map.has(item.id)) {
			map.set(item.id, item);
		}
	});
	return Array.from(map.values());
};

export default RegulationDetail;
